import type { ReactNode } from 'react';
import { Button, Tag } from './ui';
import type { TagTone } from './ui';

/**
 * DOCUMENT CHECKLIST — one process's documents with their live states
 * (spec 2026-08-26 §6). Purely presentational, like everything in this
 * package: data and callbacks arrive via props, no fetch, no router, no
 * clock, no react-dom. The same component renders in super-admin (admin
 * actions: send / resend / remove) and in platform-app (tenant actions:
 * sign / view / download) — which actions exist is decided by which
 * callbacks the host passes on each row.
 */

export interface ChecklistRow {
  itemId: string;
  title: string;
  kind: 'agreement' | 'policy';
  state:
    | 'awaiting_data' | 'ready' | 'sent' | 'partially_signed'
    | 'executed' | 'accepted' | 'declined' | 'voided' | 'expired' | 'removed';
  required: boolean;
  /** Names of merge fields still missing — rendered only for awaiting_data. */
  missingFields?: string[];
  removedReason?: string;
  /** Per-row actions; a row renders only the buttons whose callback exists. */
  onPrimary?: () => void;
  primaryLabel?: string;
  onSecondary?: () => void;
  secondaryLabel?: string;
}

export const CHECKLIST_STATE_LABEL: Record<ChecklistRow['state'], string> = {
  awaiting_data: 'Awaiting data',
  ready: 'Ready to send',
  sent: 'Sent, unsigned',
  partially_signed: 'Partially signed',
  executed: '✍ Signed',
  accepted: 'Accepted',
  declined: 'Declined',
  voided: 'Voided',
  expired: 'Expired',
  removed: 'Removed',
};

/** Mapped onto the package's existing tone palette — no new colors invented. */
export const CHECKLIST_STATE_TONE: Record<ChecklistRow['state'], TagTone> = {
  awaiting_data: 'review',
  ready: 'draft',
  sent: 'review',
  partially_signed: 'review',
  executed: 'active',
  accepted: 'active',
  declined: 'retired',
  voided: 'retired',
  expired: 'retired',
  removed: 'draft',
};

export interface DocumentChecklistProps {
  rows: ChecklistRow[];
  /** Rendered under the list when present — the host's completeness note. */
  footnote?: ReactNode;
  emptyText?: string;
}

export function DocumentChecklist({ rows, footnote, emptyText }: DocumentChecklistProps) {
  if (!rows.length) {
    return (
      <p className="text-[12.5px] text-stone-500">
        {emptyText ?? 'No documents attached to this process.'}
      </p>
    );
  }
  return (
    <div>
      <ul className="divide-y divide-dark-700 rounded-xl border border-dark-700 bg-white">
        {rows.map((row) => (
          <li key={row.itemId} className={`flex flex-wrap items-center gap-3 px-4 py-3 ${row.state === 'removed' ? 'opacity-50' : ''}`}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-stone-900">
                {row.title}
                {!row.required && <span className="ml-2 text-[11px] font-normal text-stone-500">optional</span>}
              </p>
              <p className="text-[11.5px] text-stone-500">
                {row.kind === 'agreement' ? 'Agreement — signed' : 'Policy — accepted'}
              </p>
              {row.state === 'awaiting_data' && !!row.missingFields?.length && (
                <p className="mt-1 text-[11.5px] text-amber-300">
                  Missing: {row.missingFields.join(', ')}
                </p>
              )}
              {row.state === 'removed' && row.removedReason && (
                <p className="mt-1 text-[11.5px] text-stone-500">Removed: {row.removedReason}</p>
              )}
            </div>
            <Tag tone={CHECKLIST_STATE_TONE[row.state]}>{CHECKLIST_STATE_LABEL[row.state]}</Tag>
            {row.onSecondary && (
              <Button onClick={row.onSecondary}>{row.secondaryLabel ?? 'View'}</Button>
            )}
            {row.onPrimary && (
              <Button variant="primary" onClick={row.onPrimary}>{row.primaryLabel ?? 'Open'}</Button>
            )}
          </li>
        ))}
      </ul>
      {footnote && <p className="mt-2 text-[11.5px] text-stone-500">{footnote}</p>}
    </div>
  );
}
